


import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  Paper, 
  Chip, 
  Typography,
  Tooltip,
  Box
} from '@mui/material';
import {
  UserIcon,
  PencilIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PlayIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import DOMPurify from 'dompurify';
import LabelChip from '../chips/LabelChip';
import renderTestData from '../../utils/testData';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { MissingData } from "../../enums/enumRegistry";
import { useTheme } from '@mui/material/styles';
import { 
  resetAttachments,   
  setCurrentAttachments, 
} from "../../features/attachment/attachmentsSlice";
import AttachmentViewerS3 from '../Attachment/AttachmentViewerS3';
import { isEmptyHtml } from '../../utils/string';
import { breakpoints } from "../../theme/theme";
import { convertRawToPlaintext } from '../../utils/richTextEditorHelperFunction';

const containerStyle = {
  border: '1px solid rgb(222, 226, 233)',
  borderRadius: '8px',
  padding: '16px 14px',
  background: 'white',
  [`@media (min-width:${breakpoints.xl}px)`]: {
    padding: '22px',
  },
};

// Centralized styles
const styles = {
  container: {
    padding: '16px',
    maxWidth: '100%',
    overflow: 'hidden',
    // backgroundColor: '#f5f5f5',
  },
  mainSection: {
    display: 'flex',
    width: '100%',
    marginBottom: '16px',
    gap: '16px',
  },
  leftColumn: {
    flex: 5,
    ...containerStyle,
    minWidth: 0,
    overflow: 'hidden',
    wordBreak: 'break-word',
  },
  rightColumn: {
    flex: 2,
    ...containerStyle,
    display: 'flex',
    flexDirection: 'column',
  },
  attachmentsSection: {
    width: '100%',
    
    border: '1px solid rgb(222, 226, 233)',
    borderRadius: '8px',
    // backgroundColor: '#ffffff',
    // borderRadius: '15px',
    // padding: '16px',
    // border: '3px solid #F3EDF7',
  },
  sectionTitle: {
    marginBottom: '16px',
    fontSize: '22px',
  },
  
  infoItem: {
    marginBottom: '12px',
    [`@media (min-width:${breakpoints.xl}px)`]: {
      marginBottom: '20px',
    },
  },
  infoIcon: {
    color: '#757575',
    fontSize: '12px',
    width: '12px',
    height: '12px',
    padding: '10px',
    [`@media (min-width:${breakpoints.xl}px)`]: {
      paddingTop: '20px',
    },
  },
  dateSubtext: {
    color: '#4f5154',
  },
  // New styles for improved structure
  detailItemContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
  },
  detailIconContainer: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    paddingTop: '2px',
  },
  detailContentContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  detailTitleContentContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  detailTitle: {
    marginBottom: '4px',
  },
  summarySectionContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  summaryContentContainer: {
    color: '#4f5154',
  },
  mainContentContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  detailsSectionContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  detailsHeaderContainer: {
    marginBottom: '12px',
    [`@media (min-width:${breakpoints.xl}px)`]: {
      marginBottom: '18px',
    },
  },
  detailsContentContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  labelContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '8px',
  },
  testDataContainer: {
    backgroundColor: '#f9f9f9',
    padding: '16px',
    borderRadius: '4px',
    marginTop: '16px',
    maxHeight: '300px',
    overflow: 'auto',
  },
  expandButton: {
    border: 'none',
    background: 'none',
    color: '#2160f3',
    cursor: 'pointer',
    padding: '4px 0',
    marginTop: '4px',
    '&:hover': {
      textDecoration: 'underline',
    },
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
};

const getDataOrDefault = (data) => (data == null ? MissingData.NO_DATA : data);

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const timeAgo = (dateString) => {
  const now = new Date();
  const date = new Date(dateString);
  const diffInMs = now - date;
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  if (diffInMinutes < 1) {
    return 'Just now';
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes} min ago`;
  } else {
    return formatDate(dateString);
  }
};

const CollapsibleText = ({ text, lineLimit = 8 }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldShowToggle, setShouldShowToggle] = useState(false);
  const textRef = useRef(null);
  const theme = useTheme();
  const sanitizedText = DOMPurify.sanitize(text);

  useEffect(() => {
    const checkOverflow = () => {
      if (textRef.current) {
        const fullHeight = textRef.current.scrollHeight;
        const lineHeight = parseFloat(getComputedStyle(textRef.current).lineHeight);
        const clampedHeight = lineLimit * lineHeight;
        setShouldShowToggle(fullHeight > clampedHeight);
      }
    };

    checkOverflow();

    // Recheck on window resize
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [sanitizedText, lineLimit]);

  return (
    <Box>
      <Typography
        variant="text2"
        ref={textRef}
        dangerouslySetInnerHTML={{ __html: sanitizedText }}
        sx={{
          display: '-webkit-box',
          WebkitLineClamp: isExpanded ? 'none' : lineLimit,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      />
      {shouldShowToggle && (
        <Box
          component="button"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
          aria-label={isExpanded ? 'Show less content' : 'Show more content'}
          sx={{
            mt: isExpanded ? 0.5 : 1,
            background: 'none',
            border: 'none',
            color: theme.palette.customColor.blue[600],
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            p: 0,
            fontSize: '12px',
            fontWeight: 500,
            lineHeight: '16px',
            position: 'relative',
            [`@media (min-width:${breakpoints.xl}px)`]: {
              fontSize: '14px',
              lineHeight: '20px',
              gap: 0.75,
            },
            '&:hover': {
              color: theme.palette.customColor.blue[700],
            },
            '&:focus': {
              outline: '2px solid',
              outlineColor: theme.palette.customColor.blue[300],
              outlineOffset: '2px',
              borderRadius: '4px',
            },
            '&:focus:not(:focus-visible)': {
              outline: 'none',
            },
          }}
        >
          <Typography 
            component="span"
            sx={{
              fontSize: 'inherit',
              fontWeight: 'inherit',
              lineHeight: 'inherit',
            }}
          >
            {isExpanded ? 'Show Less' : 'Show More'}
          </Typography>
          {isExpanded ? (
            <ChevronUpIcon 
              className="chevron-icon"
              style={{ 
                width: '14px', 
                height: '14px',
              }} 
            />
          ) : (
            <ChevronDownIcon 
              className="chevron-icon"
              style={{ 
                width: '14px', 
                height: '14px',
              }} 
            />
          )}        
        </Box>
      )}
    </Box>
  );
};

const parseTestData = (testData) => {
  return(
    <ul> 
      {Object.entries(testData).map(([key, value]) => (
        <li key={key}>
          <Typography component="span" variant="text2">{key}: {renderTestData(value)}</Typography>
        </li>
      ))}
    </ul> 
  );
};

// Reusable detail item component
const DetailItem = ({ icon, title, content, dateSubtext }) => {
  return (
    <Box sx={styles.infoItem}>
      {/* Main container with icon and details in flex */}
      <Box sx={styles.detailItemContainer}>
        {/* Icon container */}
        <Box sx={styles.detailIconContainer}>
          {React.cloneElement(icon, { sx: styles.infoIcon, width: '12px', height: '12px', className: 'icon' })}
        </Box>
        
        {/* Details container */}
        <Box sx={styles.detailContentContainer}>
          {/* Title and content container */}
          <Box sx={styles.detailTitleContentContainer}>
            <Typography component="p" variant="context" sx={styles.detailTitle}>
              {title}
            </Typography>
            <Typography component="span" variant="text2" sx={{ color: '#4f5154' }}>
              {content}
            </Typography>
            {dateSubtext && (
              <Typography component="p" variant="text3" sx={styles.dateSubtext}>
                {dateSubtext}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// Reusable section component for the left column sections
const SummarySection = ({ title, children }) => {
  return (
    <Box sx={styles.infoItem}>
      {/* Main container with title and content in flex column */}
      <Box sx={styles.summarySectionContainer}>
        {/* Title section */}
        <Box sx={styles.detailsHeaderContainer}>
          <Typography component="p" variant="text1">
            {title}
          </Typography>
        </Box>
        
        {/* Content section */}
        <Box sx={styles.summaryContentContainer}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

// function to render labels
const renderLabels = (labels) => {
  if (!labels || labels.length === 0) {
    return getDataOrDefault();
  }

  return labels.map((label) => (
    <Tooltip
      key={label.id || label.name}
      title={label.name || ''}
      placement="top"
      arrow
    >
      <Box
        sx={{
          maxWidth: 150, // adjust max width
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        <LabelChip
          label={label}
          sx={{
            padding: '15px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        />
      </Box>
    </Tooltip>
  ));
};

const TestCaseDescription = ({ testCaseData }) => {
  const formatAttachmentID = (data) => {
    const ids = data.map(item => item.id);
    return ids;
  };

  const dispatch = useDispatch();
  const { 
    currentAttachments,
  } = useSelector((state) => state.attachments);

  useEffect(() => {
    dispatch(resetAttachments());

    if (testCaseData?.attachments) {
      dispatch(setCurrentAttachments(formatAttachmentID(testCaseData.attachments)));
    }
  }, [testCaseData, dispatch]);

  // Define details data for reuse
  const detailsData = [
    {
      icon: <UserIcon className="icon-small" />,
      title: 'Created By',
      content: getDataOrDefault(testCaseData.generated_by_user?.username),
      dateSubtext: `on ${formatDate(testCaseData.created_at)}`
    },
    {
      icon: <PencilIcon className="icon-small" />,
      title: 'Updated By',
      content: getDataOrDefault(testCaseData.updated_by_user?.username),
      dateSubtext: `on ${formatDate(testCaseData.updated_at)}`
    },
    {
      icon: <ExclamationTriangleIcon className="icon-small" />,
      title: 'Priority',
      content: getDataOrDefault(testCaseData.priority)
    },
    {
      icon: <ClockIcon className="icon-small" />,
      title: 'Status',
      content: getDataOrDefault(testCaseData.status)
    },
    {
      icon: <PlayIcon className="icon-small" />,
      title: 'Execution Mode',
      content: getDataOrDefault(testCaseData.execution_mode)
    },
    {
      icon: <CheckCircleIcon className="icon-small" />,
      title: 'Estimated Time',
      content: `${testCaseData.estimated_duration || 15} minutes`,
    },
  ];

  const htmlDescription = convertRawToPlaintext(testCaseData?.description)

  return (
    <Box sx={styles.container}>
      {/* Main section with 80:20 split */}
      <Box sx={styles.mainSection}>
        {/* Left Column - Test Case Summary (80%) */}
        <Box sx={styles.leftColumn}>
          {/* <Typography sx={styles.sectionTitle}>Test Case Summary: {testCaseData.title}</Typography> */}
          
          {/* Description */}
          <SummarySection title="Description">
          <Box>

            {!isEmptyHtml(htmlDescription) ? (
              <CollapsibleText text={htmlDescription} />
            ) : (
              <Typography component="p" variant="text2">{MissingData.NO_DATA}</Typography>
            )}
            </Box>
          </SummarySection>
        </Box>
        
        {/* Right Column - Details (20%) */}
        <Box sx={styles.rightColumn}>
          {/* Details section with proper structure */}
          <Box sx={styles.detailsSectionContainer}>
            {/* Details header */}
            <Box sx={styles.detailsHeaderContainer}>
              <Typography component="p" variant="text1">
                Details
              </Typography>
            </Box>
            
            {/* Details content */}
            <Box sx={styles.detailsContentContainer}>
              {detailsData.map((item, index) => (
                <DetailItem
                  key={index}
                  icon={item.icon}
                  title={item.title}
                  content={item.content}
                  dateSubtext={item.dateSubtext}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
      
      {/* Attachments Section - Full Width */}
      <Box>
        {/* <AttachmentViewer 
          attachments={currentAttachments} 
        /> */}
        
        <AttachmentViewerS3
          attachments={testCaseData?.attachments || []}
          canEdit={false}
        />
      </Box>
    </Box>
  );
};

export default TestCaseDescription;